import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { ConnectionTestAlert } from '@/components/common/ConnectionTestAlert';
import { Separator } from '@/components/ui/separator';
import { Copy, Check, Info, CheckCircle2, AlertCircle, Pencil, PlugZap, Wand2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import { SourceTablesTable } from './SourceTablesTable';
import { EditSourceDialog } from './EditSourceDialog';
import { CardHeaderActions, type ActionItem } from '@/components/common/CardHeaderActions';
import { usePostSourcesByIdTestConnection } from '@/api/control/control-plane/control-plane';
import { useError } from '@/contexts/ErrorContext';
import type { SourceDto } from '@/api/control/missionImpactAPI.schemas';

/**
 * Props for the SourceCard component
 */
interface SourceCardProps {
  /** Organization ID */
  orgId: string;
  /** Profile ID */
  profileId: string;
  /** Source data from profile (null if not configured) */
  source: SourceDto | null | undefined;
  /** Callback when connection test succeeds (to refresh data) */
  onTestSuccess?: () => void;
  /** URL to resume the setup wizard when source is missing */
  resumeSetupUrl?: string;
}

/**
 * Card component for displaying a single source
 */
export function SourceCard({
  orgId,
  profileId,
  source,
  onTestSuccess,
  resumeSetupUrl,
}: SourceCardProps) {
  const { t } = useLanguage();
  const { handleApiError } = useError();
  const navigate = useNavigate();
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [connectionTestResult, setConnectionTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const { mutate: testConnection, isPending: isTesting } = usePostSourcesByIdTestConnection({
    mutation: {
      onSuccess: () => {
        setConnectionTestResult({
          success: true,
          message: t.profiles.sourceCard.connectionSuccessful,
        });
        toast.success(t.profiles.sourceCard.testSuccessful);
        onTestSuccess?.();
      },
      onError: (error: unknown) => {
        const { message } = handleApiError(error, { context: 'SourceCard.testConnection' }, { showToast: false });
        setConnectionTestResult({ success: false, message });
      },
    },
  });

  const handleCopy = (text: string, fieldName: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopiedField(fieldName);
        toast.success(t.profiles.sourceCard.copiedToClipboard);
        setTimeout(() => { setCopiedField(null); }, 2000);
      })
      .catch(() => {
        toast.error(t.profiles.sourceCard.failedToCopy);
      });
  };

  const handleTestConnection = () => {
    if (!source?.id) return;
    setConnectionTestResult(null);
    testConnection({ orgId, profileId, sourceId: source.id });
  };

  // Build actions based on source state
  const actions: ActionItem[] = source
    ? [
        {
          key: 'test',
          label: t.profiles.sourceCard.testConnection,
          icon: PlugZap,
          onClick: handleTestConnection,
          isLoading: isTesting,
          tooltip: t.profiles.sourceCard.testConnection,
        },
        {
          key: 'edit',
          label: t.sources.editDialog.buttonText,
          icon: Pencil,
          onClick: () => { setEditDialogOpen(true); },
          tooltip: t.sources.editDialog.buttonText,
        },
      ]
    : resumeSetupUrl
      ? [
          {
            key: 'resume',
            label: t.profileDetail.actions.resumeSetup,
            icon: Wand2,
            onClick: () => { void navigate(resumeSetupUrl); },
            tooltip: t.profileDetail.actions.resumeSetup,
          },
        ]
      : [];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl font-semibold">{t.profiles.sourceCard.title}</CardTitle>
            <CardDescription>{t.profiles.sourceCard.description}</CardDescription>
          </div>
          <CardHeaderActions actions={actions} />
        </div>
      </CardHeader>

      {source && (
        <EditSourceDialog
          orgId={orgId}
          profileId={profileId}
          source={source}
          externalOpen={editDialogOpen}
          onExternalOpenChange={setEditDialogOpen}
        />
      )}
      <CardContent>
        {!source ? (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>{t.profiles.sourceCard.noSourceAlert}</AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-4">
            <div className="space-y-3">
              {/* First row: Type, Base URL, Created */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-3">
                <div className="min-w-0">
                  <label className="text-sm font-medium text-muted-foreground block mb-1">
                    {t.profiles.sourceCard.type}
                  </label>
                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium background-neutral text-neutral rounded-full">
                    {source.$type}
                  </span>
                </div>

                {source.$type === 'BusinessCentral' ? (
                  <>
                    <div className="min-w-0">
                      <label className="text-sm font-medium text-muted-foreground block mb-1">
                        {t.wizard.source.bcEnvironment}
                      </label>
                      <span className="text-sm">
                        {source.environment}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <label className="text-sm font-medium text-muted-foreground block mb-1">
                        {t.wizard.source.company}
                      </label>
                      <span className="text-sm">
                        {source.companyName}
                      </span>
                    </div>
                  </>
                ) : source.$type === 'Salesforce' ? (
                  <div className="min-w-0 flex-1">
                    <label className="text-sm font-medium text-muted-foreground block mb-1">
                      {t.wizard.source.instanceUrl}
                    </label>
                    <div className="group flex items-center gap-2">
                      <span className="text-sm break-all">
                        {source.instanceUrl}
                      </span>
                      {source.instanceUrl && (
                        <button
                          onClick={() => {
                            handleCopy(source.instanceUrl, 'sourceBaseUrl');
                          }}
                          className="flex-shrink-0 p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:background-neutral rounded"
                          title={t.profiles.sourceCard.copyBaseUrl}
                        >
                          {copiedField === 'sourceBaseUrl' ? (
                            <Check className="h-4 w-4 text-success" />
                          ) : (
                            <Copy className="h-4 w-4 text-neutral" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="min-w-0 flex-1">
                    <label className="text-sm font-medium text-muted-foreground block mb-1">
                      {t.profiles.sourceCard.baseUrl}
                    </label>
                    <div className="group flex items-center gap-2">
                      <span className="text-sm break-all">
                        {source.baseUrl}
                      </span>
                      {source.baseUrl && (
                        <button
                          onClick={() => {
                            handleCopy(source.baseUrl, 'sourceBaseUrl');
                          }}
                          className="flex-shrink-0 p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:background-neutral rounded"
                          title={t.profiles.sourceCard.copyBaseUrl}
                        >
                          {copiedField === 'sourceBaseUrl' ? (
                            <Check className="h-4 w-4 text-success" />
                          ) : (
                            <Copy className="h-4 w-4 text-neutral" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                )}

                <div className="min-w-0">
                  <label className="text-sm font-medium text-muted-foreground block mb-1">
                    {t.profiles.sourceCard.created}
                  </label>
                  <div className="text-sm">
                    {source.createdUtc
                      ? new Date(source.createdUtc).toLocaleDateString()
                      : t.profiles.sourceCard.notAvailable}
                  </div>
                </div>
              </div>

              {/* Second row: Connection Status, Last Test, and empty column */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-3">
                <div className="min-w-0">
                  <label className="text-sm font-medium text-muted-foreground block mb-1">
                    {t.profiles.sourceCard.connectionStatus}
                  </label>
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${
                      source.connectionStatus === 'Success'
                        ? 'background-success text-success border border-success'
                        : 'background-error text-error border border-error'
                    }`}
                  >
                    {source.connectionStatus === 'Success' ? (
                      <CheckCircle2 className="h-3 w-3" />
                    ) : (
                      <AlertCircle className="h-3 w-3" />
                    )}
                    {source.connectionStatus}
                  </span>
                </div>

                <div className="min-w-0">
                  <label className="text-sm font-medium text-muted-foreground block mb-1">
                    {t.profiles.sourceCard.lastTest}
                  </label>
                  <div className="text-sm">
                    {source.connectionLastCheckedUtc
                      ? new Date(source.connectionLastCheckedUtc).toLocaleString()
                      : t.profiles.sourceCard.neverTested}
                  </div>
                </div>

                {/* Empty column for alignment */}
                <div></div>
              </div>

              {/* Third row: Salesforce-specific fields (Bulk API status, REST API usage, last checked) */}
              {source.$type === 'Salesforce' && (() => {
                const sfSource = source;
                return (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-3">
                    <div className="min-w-0">
                      <label className="text-sm font-medium text-muted-foreground block mb-1">
                        {t.profiles.sourceCard.bulkApi}
                      </label>
                      {sfSource.isBulkApiAvailable != null ? (
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${
                            sfSource.isBulkApiAvailable
                              ? 'background-success text-success border border-success'
                              : 'background-error text-error border border-error'
                          }`}
                        >
                          {sfSource.isBulkApiAvailable ? (
                            <CheckCircle2 className="h-3 w-3" />
                          ) : (
                            <AlertCircle className="h-3 w-3" />
                          )}
                          {sfSource.isBulkApiAvailable
                            ? t.profiles.sourceCard.bulkApiAvailable
                            : t.profiles.sourceCard.bulkApiUnavailable}
                        </span>
                      ) : (
                        <span className="text-sm text-muted-foreground">
                          {t.profiles.sourceCard.restApiUsageNotAvailable}
                        </span>
                      )}
                    </div>
                    <div className="min-w-0">
                      <label className="text-sm font-medium text-muted-foreground block mb-1">
                        {t.profiles.sourceCard.restApiUsage}
                      </label>
                      {sfSource.restApiUsagePercent != null ? (
                        <div className="flex items-center gap-3">
                          <Progress value={sfSource.restApiUsagePercent} className="flex-1 h-2" />
                          <span className="text-sm text-muted-foreground whitespace-nowrap">
                            {sfSource.restApiUsagePercent.toFixed(1)}%
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">
                          {t.profiles.sourceCard.restApiUsageNotAvailable}
                        </span>
                      )}
                    </div>
                    <div className="min-w-0">
                      <label className="text-sm font-medium text-muted-foreground block mb-1">
                        {t.profiles.sourceCard.restApiUsageLastChecked}
                      </label>
                      <div className="text-sm">
                        {sfSource.restApiUsageCheckedUtc
                          ? new Date(sfSource.restApiUsageCheckedUtc).toLocaleString()
                          : t.profiles.sourceCard.restApiUsageNotAvailable}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {connectionTestResult && (
              <ConnectionTestAlert
                success={connectionTestResult.success}
                message={connectionTestResult.message}
              />
            )}

            {/* Source Tables Section */}
            <Separator className="my-6" />
            <SourceTablesTable orgId={orgId} profileId={profileId} sourceId={source.id} sourceType={source.$type} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
